import domToImage from "dom-to-image";
import { RefObject, useEffect, useRef, useState } from "react";

const waitForImageLoad = (imageSelector?: string): Promise<void> => {
  if (!imageSelector) return Promise.resolve();

  const img = document.querySelector(imageSelector) as HTMLImageElement;
  if (!img) return Promise.resolve();

  return new Promise<void>((resolve) => {
    if (img.complete && img.naturalHeight !== 0) {
      resolve();
      return;
    }

    const onLoad = () => {
      img.removeEventListener("load", onLoad);
      resolve();
    };

    const onError = () => {
      img.removeEventListener("error", onError);
      resolve();
    };

    img.addEventListener("load", onLoad);
    img.addEventListener("error", onError);

    setTimeout(() => {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
      resolve();
    }, 3000);
  });
};

type DOMCaptureOptions = {
  dependencyKey: string;
  containerRef: RefObject<HTMLDivElement | null>;
  options?: { imageSelector?: string };
};

export const useDOMCapture = ({
  dependencyKey,
  containerRef,
  options,
}: DOMCaptureOptions) => {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(true);
  const previousKeyRef = useRef<string>("");

  useEffect(() => {
    if (!containerRef.current) return;

    if (previousKeyRef.current === dependencyKey) return;

    previousKeyRef.current = dependencyKey;
    setError(false);

    const captureScreenshot = async () => {
      setCapturing(true);
      setError(false);
      try {
        const container = containerRef.current;
        if (!container) return;

        await waitForImageLoad(options?.imageSelector);

        // Wait for async layout to settle
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 500);
        });

        const imageDataUrl = await domToImage.toPng(container);
        setScreenshot(imageDataUrl);
        setError(false);
      } catch {
        setScreenshot(null);
        setError(true);
      } finally {
        setCapturing(false);
      }
    };

    captureScreenshot().catch(console.error);
  }, [dependencyKey, containerRef]);

  return {
    screenshot,
    capturing,
    error,
    clear: () => setScreenshot(null),
  };
};
